import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Input } from '../../components/common/Input';
import { Spinner } from '../../components/common/Spinner';
import { useAdminAgent, useApproveAgent, useDisableAgent, useEnableAgent, useAddAgentComment, useToggleLargeWin, useUpdateAgent, useRejectAgent } from '../../hooks/useAgent';
import { agentService } from '../../services/agentService';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { Store, MapPin, Mail, Phone, Globe, CheckCircle, XCircle, MessageSquare, Shield, ShieldOff, Pencil } from 'lucide-react';

const STATUS_VARIANT: Record<string, any> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', DISABLED: 'danger' };

export const AdminAgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useAdminAgent(id!);
  const approve = useApproveAgent();
  const disable = useDisableAgent();
  const enable = useEnableAgent();
  const addComment = useAddAgentComment();
  const toggleLargeWin = useToggleLargeWin();
  const updateAgent = useUpdateAgent();
  const rejectAgent = useRejectAgent();

  const [disableComment, setDisableComment] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [comment, setComment] = useState('');
  const [commentPage, setCommentPage] = useState(1);
  const [salesPage, setSalesPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    storeAddress: '', storePrimaryActivity: '', storeContactEmail: '',
    storeContactPhone: '', businessName: '', website: '',
  });

  const agent = data?.data;

  const { data: commentsData } = useQuery({
    queryKey: ['admin', 'agents', id, 'comments', commentPage],
    queryFn: () => agentService.adminGetComments(id!, { page: commentPage, limit: 10 }),
    enabled: !!id,
  });
  const comments = commentsData?.data?.comments || [];
  const commentsPagination = commentsData?.data?.pagination;

  const { data: metricsData } = useQuery({
    queryKey: ['admin', 'agents', id, 'metrics'],
    queryFn: () => agentService.adminGetMetrics(id!),
    enabled: !!id,
  });
  const metrics = metricsData?.data;

  const { data: salesData } = useQuery({
    queryKey: ['admin', 'agents', id, 'sales', salesPage],
    queryFn: () => agentService.adminGetSales(id!, { page: salesPage, limit: 10 }),
    enabled: !!id,
  });
  const sales = salesData?.data?.sales || [];
  const salesPagination = salesData?.data?.pagination;

  useEffect(() => {
    if (agent) {
      setEditForm({
        storeAddress: agent.storeAddress || '',
        storePrimaryActivity: agent.storePrimaryActivity || '',
        storeContactEmail: agent.storeContactEmail || '',
        storeContactPhone: agent.storeContactPhone || '',
        businessName: agent.businessName || '',
        website: agent.website || '',
      });
    }
  }, [agent]);

  if (isLoading) return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  if (!agent) return <AdminLayout><p className="text-gray-400 dark:text-gray-500 text-center py-12">Agent not found.</p></AdminLayout>;

  const handleSaveEdit = () => {
    updateAgent.mutate({ id: id!, data: editForm }, { onSuccess: () => setEditing(false) });
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {agent.businessName || `${agent.user?.firstName} ${agent.user?.lastName}`}
          </h1>
          <Badge variant={STATUS_VARIANT[agent.status]}>{agent.status}</Badge>
          {agent.canPayoutLargeWins && <Badge variant="default">Super Agent</Badge>}
        </div>
        <p className="text-gray-500 dark:text-gray-400">Agent profile and management</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Profile details */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent Details</h3>
              {!editing ? (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveEdit} isLoading={updateAgent.isPending}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              )}
            </div>

            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Business Name" value={editForm.businessName} onChange={(e) => setEditForm({ ...editForm, businessName: e.target.value })} />
                <Input label="Primary Activity" value={editForm.storePrimaryActivity} onChange={(e) => setEditForm({ ...editForm, storePrimaryActivity: e.target.value })} />
                <div className="sm:col-span-2">
                  <Input label="Store Address" value={editForm.storeAddress} onChange={(e) => setEditForm({ ...editForm, storeAddress: e.target.value })} />
                </div>
                <Input label="Store Contact Email" value={editForm.storeContactEmail} onChange={(e) => setEditForm({ ...editForm, storeContactEmail: e.target.value })} />
                <Input label="Store Contact Phone" value={editForm.storeContactPhone} onChange={(e) => setEditForm({ ...editForm, storeContactPhone: e.target.value })} />
                <Input label="Website" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Store, label: 'Agent Name', value: `${agent.user?.firstName} ${agent.user?.lastName}` },
                  { icon: Mail, label: 'Account Email', value: agent.user?.email },
                  { icon: Phone, label: 'Account Phone', value: agent.user?.phone || '—' },
                  { icon: MapPin, label: 'Store Address', value: agent.storeAddress },
                  { icon: Store, label: 'Primary Activity', value: agent.storePrimaryActivity },
                  { icon: Mail, label: 'Store Email', value: agent.storeContactEmail },
                  { icon: Phone, label: 'Store Phone', value: agent.storeContactPhone },
                  { icon: Globe, label: 'Website', value: agent.website || '—' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">{value}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400 dark:text-gray-500">
              Applied {formatDate(agent.createdAt)}
              {agent.approvedAt && ` · Approved ${formatDate(agent.approvedAt)}`}
            </div>
          </Card>

          {/* Metrics */}
          {metrics && (
            <Card>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Performance Metrics</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Tickets Sold', value: metrics.totalTicketsSold },
                  { label: 'Total Revenue', value: formatCurrency(metrics.totalRevenue, metrics.currency) },
                  { label: 'Commission Earned', value: formatCurrency(metrics.totalCommissionEarned, metrics.currency) },
                  { label: 'Payouts Processed', value: metrics.totalPayoutsProcessed },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-0.5">{value}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Sales History — paginated */}
          <Card>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Sales History</h3>
            {sales.length === 0 ? (
              <p className="text-gray-400 dark:text-gray-500 text-center py-6 text-sm">No sales recorded.</p>
            ) : (
              <>
                <div className="space-y-2">
                  {sales.map((sale: any) => (
                    <div key={sale.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{sale.raffle?.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{sale.customer?.firstName} {sale.customer?.lastName} · {sale.quantity} ticket{sale.quantity > 1 ? 's' : ''}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(sale.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(sale.totalAmount, sale.raffle?.currency || 'USD')}</p>
                        <p className="text-xs text-emerald-600">+{formatCurrency(sale.commissionAmount, sale.raffle?.currency || 'USD')}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {salesPagination && salesPagination.pages > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-4">
                    <Button variant="outline" size="sm" disabled={salesPage === 1} onClick={() => setSalesPage(salesPage - 1)}>Previous</Button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Page {salesPage} of {salesPagination.pages}</span>
                    <Button variant="outline" size="sm" disabled={salesPage === salesPagination.pages} onClick={() => setSalesPage(salesPage + 1)}>Next</Button>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Internal comments — paginated */}
          {comments.length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Internal Notes</h3>
              <div className="space-y-3">
                {comments.map((c: any) => (
                  <div key={c.id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{c.authorName}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{formatDate(c.createdAt, 'MMM dd, yyyy HH:mm')}</p>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{c.text}</p>
                  </div>
                ))}
              </div>
              {commentsPagination && commentsPagination.pages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4">
                  <Button variant="outline" size="sm" disabled={commentPage === 1} onClick={() => setCommentPage(commentPage - 1)}>Previous</Button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Page {commentPage} of {commentsPagination.pages}</span>
                  <Button variant="outline" size="sm" disabled={commentPage === commentsPagination.pages} onClick={() => setCommentPage(commentPage + 1)}>Next</Button>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Right column — actions */}
        <div className="space-y-4">
          <Card>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Actions</h3>
            <div className="space-y-3">
              {agent.status === 'PENDING' && (
                <>
                  <Button fullWidth onClick={() => approve.mutate(id!)} isLoading={approve.isPending}>
                    <CheckCircle className="w-4 h-4 mr-2" />Approve Agent
                  </Button>
                  {!showRejectForm ? (
                    <Button fullWidth variant="danger" onClick={() => setShowRejectForm(true)}>
                      <XCircle className="w-4 h-4 mr-2" />Reject Application
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reason for rejection (will be visible to the applicant)" rows={3}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
                      <div className="flex gap-2">
                        <Button fullWidth variant="danger" onClick={() => { rejectAgent.mutate({ id: id!, reason: rejectReason }); setShowRejectForm(false); }} isLoading={rejectAgent.isPending} disabled={!rejectReason.trim()}>Reject</Button>
                        <Button fullWidth variant="outline" onClick={() => setShowRejectForm(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {agent.status === 'APPROVED' && (
                <>
                  {!showDisableForm ? (
                    <Button fullWidth variant="danger" onClick={() => setShowDisableForm(true)}>
                      <XCircle className="w-4 h-4 mr-2" />Disable Agent
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <textarea value={disableComment} onChange={(e) => setDisableComment(e.target.value)}
                        placeholder="Reason for disabling (optional)" rows={3}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
                      <div className="flex gap-2">
                        <Button fullWidth variant="danger" onClick={() => { disable.mutate({ id: id!, comment: disableComment }); setShowDisableForm(false); }} isLoading={disable.isPending}>Confirm</Button>
                        <Button fullWidth variant="outline" onClick={() => setShowDisableForm(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {agent.status === 'DISABLED' && (
                <Button fullWidth onClick={() => enable.mutate(id!)} isLoading={enable.isPending}>
                  <CheckCircle className="w-4 h-4 mr-2" />Re-enable Agent
                </Button>
              )}
              {agent.status === 'APPROVED' && (
                <Button fullWidth variant="outline"
                  onClick={() => toggleLargeWin.mutate({ id: id!, enabled: !agent.canPayoutLargeWins })}
                  isLoading={toggleLargeWin.isPending}>
                  {agent.canPayoutLargeWins ? <><ShieldOff className="w-4 h-4 mr-2" />Remove Super Agent</> : <><Shield className="w-4 h-4 mr-2" />Make Super Agent</>}
                </Button>
              )}
            </div>
          </Card>

          <Card>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              <MessageSquare className="inline w-4 h-4 mr-1" />Add Note
            </h3>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Internal note about this agent..." rows={4}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-sm bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none mb-3" />
            <Button fullWidth variant="outline" onClick={() => { addComment.mutate({ id: id!, comment }); setComment(''); }} isLoading={addComment.isPending} disabled={!comment.trim()}>
              Save Note
            </Button>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};
